import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { MapPin, Phone, Star, MoreVertical, X, Share2, Heart, MessageCircle } from 'lucide-react';
import { getTheme } from '../utils/themes';
import { showSuccess } from '../utils/toast';
import { isValidPhone, getTelLink, getWhatsAppLink } from '../utils/phoneUtils';
import { trackPhoneView } from '../utils/phoneLogTracker';
import { getImageUrl } from '../utils/imageUtils';

const getCategoryIconComponent = (category) => {
    if (!category) return LucideIcons.Store;
    const iconName = category.icon;
    if (iconName && LucideIcons[iconName]) {
        return LucideIcons[iconName];
    }
    const slug = (category.slug || category.name || '').toLowerCase();
    if (slug.includes('educat') || slug.includes('school') || slug.includes('college')) return LucideIcons.GraduationCap;
    if (slug.includes('health') || slug.includes('doctor') || slug.includes('hospital') || slug.includes('clinic')) return LucideIcons.HeartPulse;
    if (slug.includes('hotel') || slug.includes('restaurant') || slug.includes('food') || slug.includes('cafe')) return LucideIcons.UtensilsCrossed;
    if (slug.includes('retail') || slug.includes('shop') || slug.includes('store') || slug.includes('mall')) return LucideIcons.ShoppingBag;
    if (slug.includes('construct') || slug.includes('build')) return LucideIcons.HardHat;
    if (slug.includes('agri') || slug.includes('kisan') || slug.includes('farm')) return LucideIcons.Leaf;
    if (slug.includes('real') || slug.includes('estate') || slug.includes('property')) return LucideIcons.Building;
    if (slug.includes('service')) return LucideIcons.Wrench;
    if (slug.includes('auto') || slug.includes('car') || slug.includes('vehicle')) return LucideIcons.Car;
    if (slug.includes('law') || slug.includes('advocate') || slug.includes('legal')) return LucideIcons.Scale;
    return LucideIcons.Store;
};

const BusinessCard = ({ business, favouriteIds, onAddFavourite, onRemoveFavourite }) => {
    const [imgError, setImgError] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);
    const navigate = useNavigate();

    // Get primary image or first image, or use placeholder
    const images = business.images || [];
    const hasImages = images.length > 0;

    // Sort images by order and get primary image first
    const sortedImages = [...images].sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return a.order - b.order;
    });

    const currentImage = hasImages ? sortedImages[0] : null;

    // Generate business URL
    const businessUrl = business.category?.slug && business.slug
        ? `/${business.category.slug}/${business.slug}`
        : `/${business.businessId}/${business.slug}`;

    // Get theme colors from category
    const theme = getTheme(business.category?.theme || 'blue');

    const handleCardClick = (e) => {
        // Navigate only if the click didn't originate from an interactive element
        // But since we are using explicit semantic layout now, we can just attach this to the main clickable area
        navigate(businessUrl);
    };

    const toggleShortcuts = (e) => {
        e.stopPropagation();
        setShowShortcuts(!showShortcuts);
    };

    const handleShare = (e) => {
        e.stopPropagation();
        if (navigator.share) {
            navigator.share({
                title: business.name,
                text: `Check out ${business.name} on Digital Fatehabad!`,
                url: window.location.origin + businessUrl,
            }).catch(console.error);
        } else {
            navigator.clipboard.writeText(window.location.origin + businessUrl);
            showSuccess('Link copied to clipboard!');
        }
    };

    const handleFavouriteClick = (e) => {
        e.stopPropagation();
        if (!onAddFavourite || !onRemoveFavourite) return; // Guard

        const isFav = favouriteIds?.has((business._id || '').toString());
        if (isFav) {
            onRemoveFavourite(business._id);
        } else {
            onAddFavourite(business._id);
        }
    };

    const hasValidPhone = isValidPhone(business.phone);

    return (
        <div
            className="group flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200 overflow-hidden border border-gray-100 dark:border-gray-800 relative"
        >
            {/* Main Row Content */}
            <div className="flex flex-row items-center w-full">

                {/* Image Section */}
                <div
                    className="relative flex-shrink-0 bg-gray-100 dark:bg-gray-800 overflow-hidden cursor-pointer
                        w-16 h-16 sm:w-20 sm:h-20 rounded-xl m-2.5 sm:m-3 my-auto"
                    onClick={handleCardClick}
                >
                    {hasImages && !imgError ? (
                        <img
                            src={getImageUrl(currentImage.url)}
                            alt={currentImage.caption || `${business.name} - ${business.category?.name || 'Business'}`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={() => setImgError(true)}
                        />
                    ) : (
                        <div
                            className="w-full h-full flex flex-col items-center justify-center transition-transform duration-300 group-hover:scale-105"
                            style={{
                                backgroundColor: `${theme.colors.primary}12`,
                                color: theme.colors.primary
                            }}
                        >
                            {(() => {
                                const CatIcon = getCategoryIconComponent(business.category);
                                return (
                                    <CatIcon
                                        className="w-7 h-7 sm:w-8 sm:h-8"
                                        strokeWidth={1.75}
                                    />
                                );
                            })()}
                        </div>
                    )}
                </div>

                {/* Content Body */}
                <div className="flex-1 flex flex-col justify-center min-w-0 py-2.5 pr-2 pl-0.5 cursor-pointer" onClick={handleCardClick}>
                    <div>
                        <h3 className="text-[13px] sm:text-sm md:text-[15px] font-bold text-gray-900 dark:text-white line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors leading-snug">
                            {business.name}
                        </h3>
                        {business.category && (
                            <p className="text-[10px] sm:text-xs text-primary-600 dark:text-primary-400 font-medium mt-0.5 line-clamp-1">
                                {business.category.name}
                                {business.village && (
                                    <span className="text-emerald-600 dark:text-emerald-400 font-semibold ml-1.5">
                                        • 📍 {business.village.name || 'Village'}
                                    </span>
                                )}
                            </p>
                        )}
                        {business.address && (
                            <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 line-clamp-1 mt-0.5 sm:mt-1 flex items-center gap-1">
                                <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                                {business.address}
                            </p>
                        )}
                    </div>
                </div>

                {/* Right: Rating & More Options */}
                <div className="flex flex-col items-center justify-center pr-2 sm:pr-2.5 pl-0.5 flex-shrink-0 gap-1" onClick={(e) => e.stopPropagation()}>
                    {business.averageRating > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] sm:text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50 px-1.5 py-0.5 rounded-md border border-amber-200/70 dark:border-amber-800/50">
                            <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-current text-amber-500" />
                            {business.averageRating.toFixed(1)}
                        </span>
                    )}
                    <button
                        onClick={toggleShortcuts}
                        className="p-1.5 sm:p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition-all"
                        title={showShortcuts ? 'Close options' : 'More options'}
                        aria-label="More options"
                    >
                        {showShortcuts ? <X className="w-4 h-4 text-gray-600 dark:text-gray-300" /> : <MoreVertical className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Shortcuts Drawer (Expandable on click) */}
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${showShortcuts ? 'max-h-24 opacity-100 mt-1 pb-2.5' : 'max-h-0 opacity-0'}`}
            >
                <div className="flex items-center justify-around gap-2 px-3 pt-1 border-t border-gray-100 dark:border-gray-800">
                    {/* Call Shortcut */}
                    {hasValidPhone ? (
                        <a
                            href={getTelLink(business.phone)}
                            onClick={(e) => {
                                e.stopPropagation();
                                trackPhoneView({
                                    targetType: 'business',
                                    targetId: business._id,
                                    targetName: business.name,
                                    targetCategory: business.category?.name,
                                    viewedPhone: business.phone,
                                    action: 'call_click'
                                });
                            }}
                            className="flex flex-col items-center gap-1 p-2 rounded-xl bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 w-16 sm:w-20 hover:bg-green-100 dark:hover:bg-green-900/40 active:scale-95 transition-all"
                        >
                            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="text-[10px] font-medium">Call</span>
                        </a>
                    ) : (
                        <div className="flex flex-col items-center gap-1 p-2 rounded-xl bg-gray-50 dark:bg-gray-800/40 text-gray-400 w-16 sm:w-20 opacity-50 cursor-not-allowed">
                            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="text-[10px] font-medium">Call</span>
                        </div>
                    )}

                    {/* WhatsApp Shortcut */}
                    <a
                        href={getWhatsAppLink(business.phone) || '#'}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!getWhatsAppLink(business.phone)) {
                                e.preventDefault();
                                return;
                            }
                            trackPhoneView({
                                targetType: 'business',
                                targetId: business._id,
                                targetName: business.name,
                                targetCategory: business.category?.name,
                                viewedPhone: business.phone,
                                action: 'whatsapp_click'
                            });
                        }}
                        target={getWhatsAppLink(business.phone) ? '_blank' : undefined}
                        rel={getWhatsAppLink(business.phone) ? 'noreferrer' : undefined}
                        className={`flex flex-col items-center gap-1 p-2 rounded-xl bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 w-16 sm:w-20 hover:bg-green-100 dark:hover:bg-green-900/40 active:scale-95 transition-all ${!getWhatsAppLink(business.phone) ? 'opacity-50 pointer-events-none grayscale' : ''}`}
                    >
                        <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-[10px] font-medium">Chat</span>
                    </a>

                    {/* Favourite Shortcut */}
                    <button
                        onClick={handleFavouriteClick}
                        className={`flex flex-col items-center gap-1 p-2 rounded-xl w-16 sm:w-20 active:scale-95 transition-all ${favouriteIds?.has((business._id || '').toString())
                            ? 'bg-red-50 text-red-500 dark:bg-red-900/20 hover:bg-red-100'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                    >
                        <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${favouriteIds?.has((business._id || '').toString()) ? 'fill-current' : ''}`} />
                        <span className="text-[10px] font-medium">Save</span>
                    </button>

                    {/* Share Shortcut */}
                    <button
                        onClick={handleShare}
                        className="flex flex-col items-center gap-1 p-2 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 w-16 sm:w-20 hover:bg-blue-100 dark:hover:bg-blue-900/40 active:scale-95 transition-all"
                    >
                        <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-[10px] font-medium">Share</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BusinessCard;
